import { Heading } from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { v4 } from 'uuid';

import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { BillingPreviewDomainHeader } from 'src/routes/billing/components/Preview/BillingPreviewDomainHeader';
import {
  BillingPreviewHeader,
  PreviewMode,
} from 'src/routes/billing/components/Preview/BillingPreviewHeader';
import { ProductStorePage } from 'src/routes/billing/store/client/components/ProductStore';
import { ActiveFormSectionType } from 'src/routes/billing/store/components/ProductStoreEditor';
import {
  ProductStoreLineItem,
  ProductStoreSchema,
  ProductStoreVisibility,
} from 'src/routes/billing/store/schema';
import {
  Price,
  PriceType,
  Product,
  ProductStatus,
  useGetPricesByPortalQuery,
  useGetProductsQuery,
} from 'src/services/api/productsApi';

const STORE_NAME = 'Store name';

interface Props {
  basePortalDomain: ReturnType<typeof useCustomDomains>['basePortalDomain'];
  productStore: ProductStoreSchema;
  activeFormSection: ActiveFormSectionType | null;
}

export const ProductStorePreview: React.FC<Props> = ({
  basePortalDomain,
  productStore,
  activeFormSection,
}) => {
  const [previewMode, setPreviewMode] = React.useState(PreviewMode.Desktop);
  const { data: prices = [] } = useGetPricesByPortalQuery();
  const { data: products = [] } = useGetProductsQuery();

  const handleChangePreviewMode = (mode: PreviewMode) => {
    setPreviewMode(mode);
  };

  const formattedProductStore = useMemo(() => {
    const getFormattedProduct = (item: ProductStoreLineItem): Product => ({
      ...item,
      id: item.id || v4(),
      name: products.find((p) => p.id === item.productId)?.name || '',
      createdAt: '',
      updatedAt: '',
      imageUrls: [],
      status: ProductStatus.ACTIVE,
      taxPercentage: 0,
      priceIds: [],
      isUsed: false,
    });

    const getFormattedPrice = (
      item: ProductStoreLineItem,
      type: PriceType,
    ): Price => ({
      ...item,
      id: item.id || v4(),
      amount: Number(item.amount) * 100,
      currency: '',
      createdAt: '',
      ref: '',
      isUsed: false,
      interval: prices.find((p) => p.id === item.priceId)?.interval,
      type,
      productId: item.productId || '',
    });

    const formatItems = (items: ProductStoreLineItem[], type: PriceType) =>
      items.map((item) => ({
        ...item,
        description: item.description || '',
        product: getFormattedProduct(item),
        price: getFormattedPrice(item, type),
      }));

    return {
      ...productStore,
      id: productStore.id || '',
      displayName: productStore.displayName,
      ref: v4(),
      subscriptions: formatItems(
        productStore.subscriptions,
        PriceType.Recurring,
      ),
      oneTimeProducts: formatItems(
        productStore.oneTimeProducts,
        PriceType.OneTime,
      ),
    };
  }, [productStore]);

  return (
    <div className="px-4 lg:pl-6 md:pr-9 py-6 ">
      <div className="flex flex-col items-center w-full @lg:max-w-[1440px] ">
        <BillingPreviewHeader
          previewMode={previewMode}
          onChangePreviewMode={handleChangePreviewMode}
        />
        <div
          className={clsx(
            'w-full transition-all  duration-400 ease-in-out @container rounded-lg border border-solid border-non-hover-border bg-white',
            {
              'max-w-full': previewMode === PreviewMode.Desktop,
              'max-w-[375px]': previewMode === PreviewMode.Mobile,
            },
          )}
        >
          <BillingPreviewDomainHeader
            portalDomainUrl={`${basePortalDomain}/store`}
            showCopyIcon={
              productStore.productStorePreferences.visibility ===
              ProductStoreVisibility.Internal
            }
          />
          <div
            className={clsx({
              'relative before:content-[""] before:absolute before:top-0 before:left-0 before:h-full before:w-[3px] before:bg-purple-200':
                activeFormSection === ActiveFormSectionType.DisplayName,
              'before:hidden':
                activeFormSection === ActiveFormSectionType.Subscriptions ||
                activeFormSection === ActiveFormSectionType.OneTimeProducts,
            })}
          >
            <div className="py-3 px-6">
              <Heading
                size="sm"
                className={clsx({
                  'text-light-gray': !productStore.displayName,
                })}
              >
                {productStore.displayName || STORE_NAME}
              </Heading>
            </div>
            <div className="border-b border-solid border-t-0 border-x-0 border-gray-200 w-full" />
          </div>
          <div className=" h-[calc(100vh-450px)] @xl:h-[calc(100vh-250px)] block @sm:flex pb-[60px] @sm:pb-0 relative overflow-auto [scrollbar-gutter:stable]">
            <div className="w-full">
              <ProductStorePage
                productStore={formattedProductStore}
                onClickPurchase={() => Promise.resolve()}
                isMobilePreview={previewMode === PreviewMode.Mobile}
                isPreview
                activeFormSection={activeFormSection}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
